import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatUserDateTime, formatUTCStamp } from '../../utils/dateFormat';
import { getFileTimestamp } from '../../utils/timezone';
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  UserCog, Search, RefreshCw, Upload, Loader2, Radio, PowerOff, Clock, History,
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { csvCell } from '../../utils/csv';
import { PaginationFooter } from '../../components/shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useDebouncedValue, useSkipFirstEffect } from '../../components/shared/useUrlPagination';
import { DateRangePresetFilter } from '../../components/shared/DateRangePresetFilter';

const ROWS_PER_PAGE_DEFAULT = 50;

const formatDuration = (secs) => {
  if (secs == null) return '—';
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const isActive = (s) => !s.ended_at;

export const ImpersonationAudit = () => {
  const { api } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sticks to true after the first fetch so refetches never swap the table back
  // to the loader block (which caused a flash + focus loss when the current
  // filter left 0 rows and the user kept typing).
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [dateResetKey, setDateResetKey] = useState(0);
  const [page, setPage] = useUrlPage('page');
  const [rowsPerPage, setRowsPerPage] = useUrlPageSize('impersonation-audit', 10);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmEnd, setConfirmEnd] = useState(null);
  const [confirmBulkEnd, setConfirmBulkEnd] = useState(false);
  const [bulkEnding, setBulkEnding] = useState(false);

  const { dateFormat, timeFormat, timezone } = useGeneralSettings();
  const fmt = useMemo(() => ({ date: dateFormat, time: timeFormat, tz: timezone }), [dateFormat, timeFormat, timezone]);

  const debouncedSearch = useDebouncedValue(search, 400);

  // Server-side filter params shared by list fetch, Select All and CSV export.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (dateRange.from) params.set('from_date', new Date(dateRange.from).toISOString());
    if (dateRange.to) params.set('to_date', new Date(dateRange.to).toISOString());
    return params;
  }, [debouncedSearch, statusFilter, dateRange.from, dateRange.to]);

  const fetchSessions = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.set('skip', String((page - 1) * rowsPerPage));
      params.set('limit', String(rowsPerPage));
      const res = await api.get(`/admin/impersonation-sessions?${params.toString()}`);
      setSessions(Array.isArray(res.data?.sessions) ? res.data.sessions : []);
      setTotal(res.data?.total || 0);
      setActiveCount(res.data?.active_count || 0);
    } catch (e) {
      toast.error('Failed to load impersonation audit');
      setSessions([]);
      setTotal(0);
      setActiveCount(0);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [api, buildFilterParams, page, rowsPerPage]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Reset to page 1 whenever a filter changes (skips mount so ?page= deep-links survive).
  useSkipFirstEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, dateRange.from, dateRange.to, rowsPerPage]);

  const pageRows = sessions;
  const totalCount = total;

  const ft = useCallback((ts) => (ts ? formatUserDateTime(ts, fmt.date, fmt.time, fmt.tz) : '—'), [fmt]);
  const ftUTC = useCallback((ts) => (ts && fmt.tz !== 'UTC' && fmt.tz !== 'Etc/UTC' ? formatUTCStamp(ts) : ''), [fmt]);

  const handleForceEnd = async () => {
    const s = confirmEnd;
    setConfirmEnd(null);
    if (!s) return;
    try {
      await api.post(`/admin/impersonation-sessions/${s.id}/force-end`);
      toast.success('Impersonation session ended');
      fetchSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to end session');
    }
  };

  const handleBulkForceEnd = async () => {
    setConfirmBulkEnd(false);
    if (!selectedIds.length) return;
    setBulkEnding(true);
    try {
      const res = await api.post('/admin/impersonation-sessions/bulk-force-end', { session_ids: selectedIds });
      const { ended = 0, already_ended = 0 } = res.data || {};
      if (ended > 0) {
        toast.success(`Ended ${ended} impersonation session${ended === 1 ? '' : 's'}${already_ended ? ` (${already_ended} already ended)` : ''}`);
      } else {
        toast.info('No active sessions in the selection — nothing to end');
      }
      setSelectedIds([]);
      fetchSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to end selected sessions');
    } finally {
      setBulkEnding(false);
    }
  };

  // CSV export — honours the row selection when non-empty, otherwise exports
  // every row matching the current filters (fetched server-side across ALL pages).
  const exportCsv = useCallback(async () => {
    let rowsToExport = [];
    try {
      const params = buildFilterParams();
      params.set('skip', '0');
      params.set('limit', '10000');
      const res = await api.get(`/admin/impersonation-sessions?${params.toString()}`);
      const all = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
      rowsToExport = selectedIds.length > 0 ? all.filter((s) => selectedIds.includes(s.id)) : all;
    } catch (e) {
      toast.error('Failed to fetch rows for export');
      return;
    }
    if (!rowsToExport.length) {
      toast.info('No rows to export');
      return;
    }
    const headers = ['Admin', 'Admin Email', 'Target', 'Target Email', 'Target Role', 'Started', 'Ended', 'Duration (s)', 'Status', 'IP'];
    const rows = rowsToExport.map((s) => [
      s.admin_name || '', s.admin_email || '', s.target_name || '', s.target_email || '',
      s.target_role || '', s.started_at || '', s.ended_at || '',
      s.duration_seconds ?? '', s.status || (isActive(s) ? 'active' : 'ended'), s.started_ip || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `impersonation-audit-${getFileTimestamp()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rowsToExport.length} row${rowsToExport.length === 1 ? '' : 's'}`);
  }, [api, buildFilterParams, selectedIds]);

  const statusBadge = (s) => {
    if (isActive(s)) return <Badge className="bg-emerald-600 gap-1"><Radio className="w-3 h-3" /> Active</Badge>;
    if (s.status === 'force_ended') return <Badge variant="outline" className="border-amber-500 text-amber-600">Force-ended</Badge>;
    return <Badge variant="secondary">Ended</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="impersonation-audit-panel">
      {/* Page header — matches User Management / Device Management styling:
          rounded blue-tinted icon box, bold 2xl title, muted description. */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <UserCog className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="impersonation-audit-title">Impersonation Audit</h1>
          <p className="text-sm text-muted-foreground">Track every &ldquo;Login As&rdquo; session across the tenant</p>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search admin, target or IP…"
            className="h-8 pl-8 pr-8"
            data-testid="impersonation-audit-search"
          />
          {search && (
            <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearch('')} data-testid="impersonation-audit-clear-search">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[150px]" data-testid="impersonation-audit-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="ended">Ended</SelectItem>
          </SelectContent>
        </Select>
        {/* Date range — Event Log "All Time" preset filter (shared component). */}
        <DateRangePresetFilter value={dateRange} onChange={setDateRange} testIdPrefix="impersonation-audit-date" resetSignal={dateResetKey} />
        <Button
          variant="outline" size="sm"
          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/50 dark:hover:text-red-300"
          onClick={() => setConfirmBulkEnd(true)}
          disabled={selectedIds.length === 0 || bulkEnding}
          data-testid="impersonation-audit-bulk-end"
        >
          {bulkEnding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PowerOff className="w-4 h-4 mr-2" />}
          {selectedIds.length > 0 ? `End (${selectedIds.length})` : 'End'}
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={selectedIds.length === 0} data-testid="impersonation-audit-export">
          <Upload className="w-4 h-4 mr-2" />
          {selectedIds.length > 0 ? `Export (${selectedIds.length})` : 'Export'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setDateRange({ from: undefined, to: undefined }); setDateResetKey(k => k + 1); fetchSessions(); }} disabled={loading} data-testid="impersonation-audit-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Count badges row */}
      <div className="flex flex-wrap items-center gap-2" data-testid="impersonation-audit-counts-row">
        <Badge variant="outline" data-testid="impersonation-audit-total-count">{totalCount} Total</Badge>
        <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" data-testid="impersonation-audit-active-count">
          <Radio className="w-3 h-3" /> {activeCount} Active
        </Badge>
      </div>

      {/* Selection bar — appears when at least one row is ticked. */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="impersonation-audit-selection-bar">
          <span data-testid="impersonation-audit-selection-count">{selectedIds.length} selected</span>
{" "}
                  {selectedIds.length > 0 && (<>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
            data-testid="impersonation-audit-select-all-toggle"
            disabled={selectAllLoading}
            onClick={async () => {
              if (selectedIds.length >= total) { setSelectedIds([]); return; }
              setSelectAllLoading(true);
              try {
                const params = buildFilterParams();
                params.set('ids_only', 'true');
                const res = await api.get(`/admin/impersonation-sessions?${params.toString()}`);
                setSelectedIds(Array.isArray(res.data?.ids) ? res.data.ids : []);
              } catch (e) {
                toast.error('Failed to select all rows');
              } finally {
                setSelectAllLoading(false);
              }
            }}
          >
            {selectAllLoading ? 'Selecting…' : (selectedIds.length >= total ? 'Deselect All' : 'Select All')}
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedIds([])} data-testid="impersonation-audit-selection-clear">Clear</Button>
        </>)}
                  </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {!initialLoadDone ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="impersonation-audit-loading">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading impersonation audit…
            </div>
          ) : pageRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="impersonation-audit-empty">
              <History className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-base font-medium">No impersonation sessions found</p>
              <p className="text-xs mt-1">{(debouncedSearch || statusFilter !== 'all' || dateRange.from) ? 'No sessions match the current filters.' : '"Login As" activity will be recorded here.'}</p>
            </div>
          ) : (
            <div className={`overflow-x-auto transition-opacity ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
              <Table data-testid="impersonation-audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        data-testid="impersonation-audit-select-all"
                        checked={pageRows.length > 0 && pageRows.every((s) => selectedIds.includes(s.id))}
                        onCheckedChange={(checked) => {
                          const pageIds = pageRows.map((s) => s.id).filter(Boolean);
                          setSelectedIds((prev) => checked
                            ? Array.from(new Set([...prev, ...pageIds]))
                            : prev.filter((id) => !pageIds.includes(id)));
                        }}
                      />
                    </TableHead>
                    <TableHead>Admin (who)</TableHead>
                    <TableHead>Target (whom)</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Ended</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((s) => (
                    <TableRow key={s.id} className={isActive(s) ? 'bg-emerald-50/60 dark:bg-emerald-950/20' : ''} data-testid={`impersonation-audit-row-${s.id}`}>
                      <TableCell>
                        <Checkbox
                          data-testid={`impersonation-audit-select-${s.id}`}
                          checked={selectedIds.includes(s.id)}
                          onCheckedChange={(checked) => {
                            setSelectedIds((prev) => checked ? [...prev, s.id] : prev.filter((id) => id !== s.id));
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{s.admin_name || s.admin_email}</div>
                        <div className="text-xs text-muted-foreground">{s.admin_email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{s.target_name || s.target_email}</div>
                        <div className="text-xs text-muted-foreground">{s.target_email} · {String(s.target_role || '').replace('_', ' ')}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div>{ft(s.started_at)}</div>
                        {ftUTC(s.started_at) && <div className="text-[10px] text-muted-foreground">{ftUTC(s.started_at)}</div>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div>{ft(s.ended_at)}</div>
                        {ftUTC(s.ended_at) && <div className="text-[10px] text-muted-foreground">{ftUTC(s.ended_at)}</div>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-muted-foreground" />{formatDuration(s.duration_seconds)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {statusBadge(s)}
                          {/* Force-end moved here from the removed Action column —
                              only shown while the session is still active. */}
                          {isActive(s) && (
                            <Button
                              variant="outline" size="sm"
                              className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/50 dark:hover:text-red-300"
                              onClick={() => setConfirmEnd(s)}
                              data-testid={`impersonation-audit-force-end-${s.id}`}
                            >
                              <PowerOff className="w-3 h-3 mr-1" /> End
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.started_ip || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination footer — always mounted so the card height stays the
              same across loading / empty / populated states. */}
          <PaginationFooter
            page={page}
            setPage={setPage}
            totalItems={total}
            itemsPerPage={rowsPerPage}
            setItemsPerPage={setRowsPerPage}
            testIdPrefix="impersonation-audit"
            disabled={loading}
            alwaysShow
          />
        </CardContent>
      </Card>

      {/* Bulk force-end confirmation */}
      <AlertDialog open={confirmBulkEnd} onOpenChange={setConfirmBulkEnd}>
        <AlertDialogContent data-testid="impersonation-audit-bulk-end-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>End Selected Impersonation Sessions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will end all <strong>active</strong> sessions among the {selectedIds.length} selected row{selectedIds.length === 1 ? '' : 's'} and mark them in the audit trail. Already-ended sessions are skipped.
              Note: users' already-open sessions may remain active until their tokens expire.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impersonation-audit-bulk-end-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkForceEnd} className="bg-red-600 hover:bg-red-700" data-testid="impersonation-audit-bulk-end-confirm">
              End sessions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force-end confirmation */}
      <AlertDialog open={!!confirmEnd} onOpenChange={(o) => !o && setConfirmEnd(null)}>
        <AlertDialogContent data-testid="impersonation-audit-force-end-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>End This Impersonation Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks the session as ended in the audit trail
              {confirmEnd && <> for <strong>{confirmEnd.admin_email}</strong> → <strong>{confirmEnd.target_email}</strong></>}.
              Note: the user's already-open session may remain active until their token expires.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impersonation-audit-force-end-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleForceEnd} className="bg-red-600 hover:bg-red-700" data-testid="impersonation-audit-force-end-confirm">
              End session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ImpersonationAudit;
